import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { DOT_RGB_YELLOW, PlateCanvas } from 'classes/plate-canvas';
import { ErrorBoundary } from 'components/common/error-boundary';
import { AimingContext } from 'contexts/aiming.context';
import { AuthContext } from 'contexts/auth.context';
import { isAfter, parseISO } from 'date-fns';
import { BASEBALL_PNG_PX, DOT_SIZE_SM, SHOT_OPACITY_DELTA } from 'enums/canvas';
import { TrainStep } from 'enums/training.enums';
import { isAppearanceDark } from 'index';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { TrainingMode } from 'lib_ts/enums/machine.enums';
import { IPitch, IPlateLoc, IPlateLocExt } from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { useContext, useEffect, useRef, useState } from 'react';
import Slider from 'react-input-slider';

const COMPONENT_NAME = 'TrainingPlateView';

// how long to wait for all canvas changes before updating img src
const REDRAW_DEQUEUE_MS = 500;

/** seconds to wait before refiring without manual adjustment input */
const AUTO_RETRY_QUICK_SEC = 10;

const DEFAULT_SLIDER: IPlateLoc = {
  plate_x: 0,
  plate_z: 0,
};

interface IProps extends Partial<IPlateLoc> {
  step: TrainStep;

  pitch: IPitch;
  shots: IMachineShot[];
  newShotID?: string;

  // irrelevant for manual training
  onUpdateShot: (shot: IMachineShot | undefined) => void;
}

export const TrainingPlateView = (props: IProps) => {
  const { current } = useContext(AuthContext);
  const { manualPlate, setManualPlate } = useContext(AimingContext);

  const [mountDate] = useState(new Date());

  const [canvasDef] = useState(PlateCanvas.makeLandscape(isAppearanceDark()));

  const retryInterval = useRef<NodeJS.Timer>();

  // what is used for assembly of the results
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const mainCanvas = useRef<HTMLCanvasElement>(null);
  const shotsCanvas = useRef<HTMLCanvasElement>(null);

  // what gets seen by users + LogRocket
  const [imgSrc, setImgSrc] = useState<string>();
  // for dequeuing updates to the imgSrc
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const updateTimeout = useRef<NodeJS.Timeout>();

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    updateTimeout.current = setTimeout(async () => {
      console.debug(`${COMPONENT_NAME}: update imgSrc`);

      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [mainCanvas.current, shotsCanvas.current].filter(
        (c) => c
      ) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  // drawMain
  useEffect(() => {
    try {
      const canvas = mainCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      canvasDef.drawStrikeZoneAutoAdjustArea(ctx);
      canvasDef.drawStrikeZone(ctx);
      canvasDef.drawGround(ctx);
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, []);

  useEffect(() => {
    const intervalCallback = () => {
      if (current.training_mode !== TrainingMode.Manual) {
        clearInterval(retryInterval.current);

        let counter = AUTO_RETRY_QUICK_SEC;

        retryInterval.current = setInterval(() => {
          if (counter > 0) {
            counter--;
            return;
          }

          clearInterval(retryInterval.current);
          props.onUpdateShot(undefined);
        }, 1_000);
      }
    };

    switch (props.step) {
      case TrainStep.ManualAdjust:
      case TrainStep.ManualInput: {
        intervalCallback();
        setManualPlate({ ...DEFAULT_SLIDER });
        break;
      }

      case TrainStep.Complete: {
        setManualPlate({
          plate_x: canvasDef.CONFIG.x.default_ft,
          plate_z: canvasDef.CONFIG.y.default_ft,
        });
        break;
      }

      default: {
        break;
      }
    }
  }, [props.step]);

  // drawComplete
  useEffect(() => {
    if (props.step !== TrainStep.Complete) {
      return;
    }

    const callback = async () => {
      try {
        console.debug('drawComplete');

        const canvas = shotsCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        await canvasDef.drawCheckmark(ctx);
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [props.step]);

  // drawShots
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = shotsCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (props.step === TrainStep.Complete) {
          return;
        }

        const sortedShots = props.shots
          .filter((m) => isAfter(parseISO(m._created), mountDate))
          .sort((a, b) => -a._created.localeCompare(b._created));

        const plates = sortedShots.map((shot) => {
          const o: IPlateLocExt = {
            ...TrajHelper.getPlateLoc(shot.traj),
            _created: shot._created,
            _id: shot._id,
          };

          return o;
        });

        for (const p of plates) {
          const emphasis = props.newShotID === p._id;
          const i = plates.findIndex((m) => m === p);
          const opacity = 1 - i * SHOT_OPACITY_DELTA;

          await canvasDef.drawDot(ctx, {
            plate: p,
            color: `rgba(${DOT_RGB_YELLOW}, ${opacity})`,
            size: DOT_SIZE_SM,
            labelText: emphasis
              ? `${p.plate_x.toFixed(1)}', ${p.plate_z.toFixed(1)}'`
              : undefined,
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [mountDate, props.step, props.shots]);

  /** width and height on the canvas element are not the same as its style width and height
   * affects zoom/scaling of the drawings when there are differences
   */
  return (
    <ErrorBoundary componentName={COMPONENT_NAME}>
      <div className="slider">
        <div
          className="sizer"
          style={{
            aspectRatio: '1.6',
            maxHeight: '400px',
            minHeight: '100px',
          }}
        >
          {/* for log rocket recordings to show the results */}
          {imgSrc && <img className="image-wrapper" src={imgSrc} />}
          <canvas
            ref={mergeCanvas}
            data-identifier="merge-canvas"
            width={canvasDef.CONFIG.canvas.width_px}
            height={canvasDef.CONFIG.canvas.height_px}
          />
          <canvas
            ref={mainCanvas}
            data-identifier="main-canvas"
            width={canvasDef.CONFIG.canvas.width_px}
            height={canvasDef.CONFIG.canvas.height_px}
          />
          <canvas
            ref={shotsCanvas}
            data-identifier="shots-canvas"
            width={canvasDef.CONFIG.canvas.width_px}
            height={canvasDef.CONFIG.canvas.height_px}
          />
          <div className="slider-wrapper animate-fade">
            <Slider
              data-testid="QuickTrainPlateLocation"
              axis="xy"
              xstep={canvasDef.CONFIG.x.step}
              xmin={canvasDef.CONFIG.x.min_ft}
              xmax={canvasDef.CONFIG.x.max_ft}
              x={manualPlate.plate_x}
              ystep={canvasDef.CONFIG.y.step}
              ymin={canvasDef.CONFIG.y.min_ft}
              ymax={canvasDef.CONFIG.y.max_ft}
              y={manualPlate.plate_z}
              onChange={(value) => {
                clearInterval(retryInterval.current);

                setManualPlate({
                  plate_x: value.x,
                  plate_z: value.y,
                });
              }}
              onDragEnd={() => {
                setLastUpdate(Date.now());
              }}
              styles={{
                track: {
                  backgroundColor: 'rgba(0, 0, 255, 0)',
                  width: '100%',
                  height: '100%',
                },
                thumb: {
                  backgroundColor: 'rgba(0, 255, 0, 0)',
                  backgroundImage: 'url(/img/baseball.png)',
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  boxShadow: 'none',
                  height: BASEBALL_PNG_PX,
                  width: BASEBALL_PNG_PX,
                  margin: -BASEBALL_PNG_PX / 2,
                  opacity: [
                    TrainStep.ManualAdjust,
                    TrainStep.ManualInput,
                  ].includes(props.step)
                    ? 1
                    : 0,
                },
              }}
              yreverse
            />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
